import { useState } from "react";
import { apiClient } from "@/App";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { PhoneCall } from "lucide-react";
import { AssistanceFields, Field, PAYMENTS, Pick, StopsEditor } from "@/components/calendar/PhoneBookingFields";
import useConflictGuard from "@/components/calendar/useConflictGuard";
import { businessToday, CHECKBOX_CLS, errorText } from "@/lib/calendar";
import { estimatePrivateFare } from "@/lib/fare";

const blank = () => ({
  client_name: "", client_phone: "", client_email: "", ride_type: "private", service_type: "one_way",
  pickup_address: "", dropoff_address: "", pickup_date: businessToday(), pickup_time: "",
  return_pickup_time: "", return_time_tbc: false, wait_and_return: false, wait_time: 0, multi: false, stops: [],
  estimated_miles: "", vehicle_type: "car", payment_method: "card", special_requests: "",
  mobility_aids: [], assistance_needs: [], needs_entry_assistance: null, mobility_aid: null, mobility_aid_other: "",
  accessibility_need: null, accessibility_other: "", has_service_animal: null, driver_id: "",
  pin_code: String(Math.floor(1000 + Math.random() * 9000)),
});

const toPayload = (f) => {
  const stops = f.multi ? f.stops.filter((s) => s.address) : [];
  const roundTrip = f.service_type === "round_trip";
  const p = {
    client_name: f.client_name, client_phone: f.client_phone, client_email: f.client_email || null,
    pickup_locations: [{ address: f.pickup_address, latitude: 0, longitude: 0, order: 0 }],
    dropoff_address: f.dropoff_address, dropoff_latitude: 0, dropoff_longitude: 0,
    pickup_date: f.pickup_date, pickup_time: f.pickup_time, vehicle_type: f.vehicle_type,
    special_requests: f.special_requests, pin_code: f.pin_code, ride_type: f.ride_type, service_type: f.service_type,
    has_multiple_stops: stops.length > 0, stops, trip_purpose: stops.length ? "errands_multiple" : null,
    wait_and_return: roundTrip && f.wait_and_return, wait_time: roundTrip && f.wait_and_return ? f.wait_time : 0,
    return_pickup_time: roundTrip && !f.wait_and_return && !f.return_time_tbc ? f.return_pickup_time || null : null,
    return_time_tbc: roundTrip && !f.wait_and_return ? f.return_time_tbc || !f.return_pickup_time : null,
    estimated_miles: Number(f.estimated_miles) || 0, driver_id: f.driver_id || null,
    mobility_aids: f.mobility_aids, assistance_needs: f.assistance_needs,
    needs_entry_assistance: f.needs_entry_assistance, mobility_aid: f.mobility_aid, mobility_aid_other: f.mobility_aid_other,
    accessibility_need: f.accessibility_need, accessibility_other: f.accessibility_other, has_service_animal: f.has_service_animal,
    payment_method: f.ride_type === "private" ? f.payment_method : null,
  };
  if (f.ride_type === "private") p.fare_estimate = estimatePrivateFare(p);
  return p;
};

export const PhoneBookingDialog = ({ open, onClose, drivers, onCreated }) => {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const { guard, conflictDialog } = useConflictGuard();
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const on = (k) => (e) => set(k, e.target.value);
  const roundTrip = f.service_type === "round_trip";
  const valid = f.client_name && f.client_phone && f.pickup_address && f.dropoff_address && f.pickup_date && f.pickup_time && f.pin_code.length === 4;
  const fare = f.ride_type === "private" ? estimatePrivateFare(toPayload(f)) : null;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = toPayload(f);
      const res = await guard((force) => apiClient.post("/dispatch/rides", { ...payload, force }));
      if (res) {
        toast.success(`Booked ${f.client_name} — added to the calendar${res.data.driver_name ? ` with ${res.data.driver_name}` : ""}`);
        setF(blank());
        onCreated?.(res.data);
        onClose();
      }
    } catch (e) {
      toast.error(errorText(e, "Failed to create booking"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="phone-booking-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PhoneCall className="w-5 h-5 text-cyan-600" />New Phone Reservation</DialogTitle>
          <DialogDescription>Entered by dispatch — saved as approved and added to the calendar automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Customer full name"><Input value={f.client_name} onChange={on("client_name")} data-testid="phone-client-name" /></Field>
            <Field label="Phone"><Input value={f.client_phone} onChange={on("client_phone")} data-testid="phone-client-phone" /></Field>
            <Field label="Email (optional, links app account)"><Input value={f.client_email} onChange={on("client_email")} data-testid="phone-client-email" /></Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Ride type"><Pick value={f.ride_type} onChange={(v) => set("ride_type", v)} options={[["private", "Private / Personal"], ["nemt", "NEMT Medical"]]} testId="phone-ride-type" /></Field>
            <Field label="Trip type"><Pick value={f.service_type} onChange={(v) => setF((x) => ({ ...x, service_type: v, wait_and_return: false, wait_time: 0 }))} options={[["one_way", "One-Way"], ["round_trip", "Round-Trip"]]} testId="phone-service-type" /></Field>
            <Field label="Vehicle"><Pick value={f.vehicle_type} onChange={(v) => set("vehicle_type", v)} options={[["car", "Car"], ["van", "Van"], ["handicap_van", "Wheelchair Van"]]} testId="phone-vehicle-type" /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Pickup address"><Input value={f.pickup_address} onChange={on("pickup_address")} data-testid="phone-pickup-address" /></Field>
            <Field label="Destination address"><Input value={f.dropoff_address} onChange={on("dropoff_address")} data-testid="phone-dropoff-address" /></Field>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Pickup date"><Input type="date" value={f.pickup_date} onChange={on("pickup_date")} data-testid="phone-pickup-date" /></Field>
            <Field label="Pickup time (ET)"><Input type="time" value={f.pickup_time} onChange={on("pickup_time")} data-testid="phone-pickup-time" /></Field>
            <Field label="Est. miles"><Input type="number" min="0" value={f.estimated_miles} onChange={on("estimated_miles")} data-testid="phone-miles" /></Field>
            <Field label="PIN"><Input value={f.pin_code} maxLength={4} onChange={(e) => set("pin_code", e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="phone-pin" /></Field>
          </div>
          {roundTrip && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2"><Checkbox className={CHECKBOX_CLS} checked={f.wait_and_return} onCheckedChange={(v) => setF((x) => ({ ...x, wait_and_return: !!v, wait_time: v ? 30 : 0 }))} data-testid="phone-wait-return" />Wait & Return</label>
                <label className="flex items-center gap-2"><Checkbox className={CHECKBOX_CLS} checked={f.multi} onCheckedChange={(v) => setF((x) => ({ ...x, multi: !!v, stops: v ? [{ address: "", wait: false, wait_minutes: 0 }] : [] }))} data-testid="phone-multi-stops" />Multiple stops</label>
              </div>
              {f.wait_and_return ? (
                <Field label="Driver waits"><Pick value={String(f.wait_time)} onChange={(v) => set("wait_time", Number(v))} options={[["30", "30 min ($20)"], ["60", "1 hour ($40)"], ["90", "1.5 hrs ($60)"]]} testId="phone-wait-time" /></Field>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3 items-end">
                  <Field label="Return pickup time (ET)"><Input type="time" value={f.return_pickup_time} disabled={f.return_time_tbc} onChange={on("return_pickup_time")} data-testid="phone-return-time" /></Field>
                  <label className="flex items-center gap-2 text-sm pb-2"><Checkbox className={CHECKBOX_CLS} checked={f.return_time_tbc} onCheckedChange={(v) => set("return_time_tbc", !!v)} data-testid="phone-return-tbc" />Return Time To Be Confirmed</label>
                </div>
              )}
              {f.multi && <StopsEditor stops={f.stops} setStops={(s) => set("stops", s)} />}
            </div>
          )}
          <AssistanceFields f={f} set={set} />
          <div className="grid sm:grid-cols-2 gap-3">
            {f.ride_type === "private" && <Field label="Payment method"><Pick value={f.payment_method} onChange={(v) => set("payment_method", v)} options={PAYMENTS} testId="phone-payment" /></Field>}
            <Field label="Assign driver (optional)"><Pick value={f.driver_id} onChange={(v) => set("driver_id", v === "none" ? "" : v)} options={[["none", "Assign later"], ...drivers.map((d) => [d.id, d.name])]} testId="phone-driver" placeholder="Assign later" /></Field>
          </div>
          <Field label="Trip notes"><Textarea rows={2} value={f.special_requests} onChange={on("special_requests")} data-testid="phone-notes" /></Field>
          <div className="flex items-center justify-between gap-3 pt-2 border-t">
            {fare !== null ? <p className="text-sm">Fare (standard rates): <b data-testid="phone-fare">${fare.toFixed(2)}</b></p> : <span className="text-sm text-slate-500">NEMT — billed per program</span>}
            <Button onClick={submit} disabled={!valid || busy} className="bg-cyan-600 hover:bg-cyan-700" data-testid="phone-booking-submit">Save Booking</Button>
          </div>
        </div>
        {conflictDialog}
      </DialogContent>
    </Dialog>
  );
};

export default PhoneBookingDialog;
